import React, { useState, useEffect } from 'react';
import { X, Upload, Building } from 'lucide-react';
import Select from 'react-select';
import countryList from 'react-select-country-list';

const EtablissementForm = ({ 
  etablissement, 
  openDialog, 
  onClose, 
  onSubmit, 
  loading, 
  formData, 
  setFormData 
}) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Initialiser la liste des pays
  useEffect(() => {
    const countryOptions = countryList().getData();
    setCountries(countryOptions);
    
    // Trouver Niger et le définir par défaut
    const niger = countryOptions.find(country => country.label === 'Niger');
    if (niger) {
      setSelectedCountry(niger);
      setFormData(prev => ({ ...prev, pays: niger.label }));
    }
  }, []);

  // Gérer la mise à jour du pays sélectionné quand les pays sont chargés
  useEffect(() => {
    if (countries.length > 0 && etablissement?.pays) {
      const existingCountry = countries.find(country => country.label === etablissement.pays);
      if (existingCountry) {
        setSelectedCountry(existingCountry);
      }
    }
  }, [countries, etablissement]);

  useEffect(() => {
    if (etablissement?.logo_url) {
      setLogoPreview(etablissement.logo_url);
    }
  }, [etablissement]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (selected) => {
    setSelectedCountry(selected);
    setFormData(prev => ({
      ...prev,
      pays: selected ? selected.label : ''
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        logo: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClose = () => {
    onClose();
    setLogoPreview(etablissement?.logo_url || null);
  };

  if (!openDialog) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#202654] to-[#2a2f6a] px-8 py-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold text-white">
              {etablissement ? 'Modifier les informations' : 'Ajouter les informations'}
            </h3>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Logo Section */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <label htmlFor="logo-upload" className="cursor-pointer group">
                <div className="relative w-40 h-40 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300 group-hover:border-[#202654] transition-all duration-200 overflow-hidden">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building className="h-16 w-16 text-gray-400" />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <Upload className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500 mt-3 text-center">
                Cliquez pour changer le logo
              </p>
            </div>

            {/* Form Fields */}
            <div className="flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Section Informations de base */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Informations de base
                  </h4>
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'établissement <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="libelle"
                    value={formData.libelle}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="Nom de l'établissement"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="Numéro de contact"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de création
                  </label>
                  <input
                    type="date"
                    name="date_creation"
                    value={formData.date_creation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Devise <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="devise"
                    value={formData.devise}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="Devise de l'établissement"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Boîte postale <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bp"
                    value={formData.bp}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="BP 123"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pays <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    placeholder="Sélectionner un pays"
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        borderColor: state.isFocused ? '#202654' : '#d1d5db',
                        borderWidth: '1px',
                        borderRadius: '0.5rem',
                        minHeight: '48px',
                        boxShadow: state.isFocused ? '0 0 0 2px rgba(32, 38, 84, 0.1)' : 'none',
                        '&:hover': {
                          borderColor: '#202654'
                        }
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#202654' : state.isFocused ? '#f3f4f6' : 'white',
                        color: state.isSelected ? 'white' : '#374151',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#202654' : '#f3f4f6'
                        }
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  <input
                    type="url"
                    name="site_web"
                    value={formData.site_web}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="https://www.exemple.com"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ministère de tutelle <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ministere"
                    value={formData.ministere}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="Ministère de tutelle"
                  />
                </div>

                <div className="md:col-span-2 form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="Adresse complète"
                  />
                </div>

                {/* Section Paramètres académiques */}
                <div className="md:col-span-2">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                    Paramètres académiques
                  </h4>
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pourcentage note contrôle (%)
                  </label>
                  <input
                    type="number"
                    name="pourcentage_note_controle"
                    value={formData.pourcentage_note_controle}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="30"
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pourcentage note examen (%)
                  </label>
                  <input
                    type="number"
                    name="pourcentage_note_examen"
                    value={formData.pourcentage_note_examen}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all duration-200"
                    placeholder="70"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 rounded-b-2xl flex justify-end space-x-4">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-[#202654] to-[#2a2f6a] text-white rounded-lg font-medium hover:from-[#2a2f6a] hover:to-[#202654] transition-all duration-200 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <svg className="spinner h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {etablissement ? 'Modification...' : 'Création...'}
              </>
            ) : (
              etablissement ? 'Enregistrer les modifications' : 'Créer l\'établissement'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EtablissementForm; 